package net.sergeych.kilogin.client

import kotlinx.coroutines.Job
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.serializer
import net.sergeych.bintools.ByteChunk
import net.sergeych.bintools.asChunk
import net.sergeych.crypto2.*
import net.sergeych.kilogin.*
import net.sergeych.kilogin.server.RestoreAccessPayload
import net.sergeych.kiloparsec.Command
import net.sergeych.kiloparsec.KiloClient
import net.sergeych.kiloparsec.RemoteInterface
import net.sergeych.mp_logger.*
import net.sergeych.mp_tools.globalLaunch

/**
 * Application might want to serialize and store updated instances of it in some
 * permanent storage to restore login data also in offline mode, and re-login
 * automatically where possible withot asking the password again.
 *
 * Kilogin client _does not store the data itself_ but only notifies client software
 * that it is changed and ought to be updated.
 */
@Serializable
data class ClientSecretData<T>(
    val loginName: String,
    val clientPrivateKey: SigningSecretKey,
    val dataKey: SymmetricKey,
    val loginData: T? = null,
    val packedSecretData: UByteArray? = null,
)

open class KiloginClientSession {
    /**
     * [KiloginClient] instance where this session is created.
     *
     * ___Important___. __Do not install [net.sergeych.kiloparsec.KiloInterface.onConnected] handlers in the head,
     * e.g. with `true` argument to it, or do not access this field from it, can cause NPE!__
     */
    lateinit var client: KiloginClient<*, *>
        internal set

    val currentLoginName: String?
        get() = when (val s = client.state.value) {
            is LoginState.LoggedIn<*> -> s.clientData.loginName
            LoginState.LoggedOut -> null
        }

    @Suppress("unused")
    inline fun <reified T> getLoginData(): T? {
        @Suppress("UNCHECKED_CAST")
        return when (val s = client.state.value) {
            is LoginState.LoggedIn<*> -> (s as LoginState.LoggedIn<T>).clientData.loginData
            else -> null
        }
    }

    @Suppress("unused")
    val isLoggedIn: Boolean get() = client.isLoggedIn

}

class KiloginClient<D, I : KiloginClientSession>(
    private val client: KiloClient<I>,
    private val localIdSecretKey: SigningSecretKey,
    private var lastKiloginData: ClientSecretData<D>? = null,
    saveState: ((UByteArray?) -> Unit)? = null,
    private val loginNameSalt: String,
    private val dataSerializer: KSerializer<D>,
) : RemoteInterface, Loggable by LogTag("SLCLI") {

    private val loginDataSerializer = ClientSecretData.serializer(dataSerializer)

    private val _state = MutableStateFlow(
        lastKiloginData?.let { LoginState.LoggedIn(it) }
            ?: LoginState.LoggedOut
    )
    val state: StateFlow<LoginState> = _state

    fun <D> loginDataOrNull(): ClientSecretData<D>? {
        val s = _state.value
        @Suppress("UNCHECKED_CAST")
        return if (s is LoginState.LoggedIn<*>)
            (s as? LoginState.LoggedIn<D>)?.clientData
        else null
    }

    fun loginNameOrNull(): String? = loginDataOrNull<D>()?.loginName

    fun loginNameKeyOrNull(): ByteChunk? = loginDataOrNull<D>()?.let { deriveLoginNameKey(it.loginName) }

    /**
     * Get secret data from ACO, which could be null. To check login state use [isLoggedIn].
     *
     * @return secret data stored in ACO on registration (or updated in it later)
     * @throws IllegalStateException if not logged in
     */
    inline fun <reified T> unpackSecretDataOrThrow(): T? = secretDataOrThrow()?.decodeBipack<T>()

    /**
     * Get packed secret data. It could be null. To check login state use [isLoggedIn].
     * @return secret data packed into ACO on registration (or updated in it later)
     * @throws IllegalStateException if not logged in
     */
    fun secretDataOrThrow(): UByteArray? = loginDataOrThrow().packedSecretData

    @Suppress("unused")
    val connectedFlow: StateFlow<Boolean> = client.connectedStateFlow

    //    private var clientState: ClientState<D>? = savedData
//        set(value) {
//            if (field != value) {
//                field = value
//                if (value == null) {
//                     do actual disconnect work
//                    _cflow.value = false
//                    _state.value = LoginState.LoggedOut
//                } else {
//                    val v = _state.value
//                    if (v !is LoginState.LoggedIn<*> || v.loginData != value) {
//                        _state.value = LoginState.LoggedIn(value)
//                    }
//                }
//            }
//        }
//
    @Suppress("UNCHECKED_CAST", "unused")
    val applicationData: D?
        get() = (state.value as? LoginState.LoggedIn<D>)?.clientData?.loginData

    private var adapterReady = MutableStateFlow<Boolean>(false)

    /**
     * The flow that tracks connection state of the client, when state is true,
     * the connection is ready to be used (either logged in or not).
     *
     * When connection drops, the state flow is set to false, but the client
     * tries to restore connection and re-login, then set it to true.
     *
     * Note that after connection restoration, logged in state could be dropped if the
     * server for some reason dropped the session (e.g. somebody else changed the password, etc).
     */
    @Suppress("unused")
    val connectionReady = adapterReady.asStateFlow()

    suspend fun waitReady() {
        adapterReady.waitFor(true)
    }

    /**
     * Call client API commands
     */
    override suspend fun <A, R> call(cmd: Command<A, R>, args: A): R {
        waitReady()
        return client.call(cmd, args)
    }

    override suspend fun <A> push(cmd: Command<A, Unit>, args: A) {
        waitReady()
        return client.push(cmd, args)
    }

    private var jobs = listOf<Job>()

    private suspend fun tryRestoreLogin() {
        lastKiloginData?.let { clientState ->
            debug { "trying to restore login with a token" }
            while (true) {
                try {
                    val ar = client.call(slLoginByConnectionId)
                    this._state.value = if (ar is AuthenticationResult.Success) {
                        LoginState.LoggedIn(
                            ClientSecretData(
                                clientState.loginName,
                                clientState.clientPrivateKey,
                                clientState.dataKey,
                                biDecode(dataSerializer, ar.extraData),
                                clientState.packedSecretData
                            )
                        )
                            .also {
                                debug { "login restored by the token: ${ar.loginNameKey} ${it.clientData.clientPrivateKey}" }
                            }
                    } else {
                        debug { "failed to restore login by the token: $ar" }
                        LoginState.LoggedOut
                    }
                    break
                } catch (t: Throwable) {
                    exception { "failed to restore login by token, will retry" to t }
                    delay(1500)
                }
            }
        }
        // loop above is running until connected
        adapterReady.value = true
    }

    init {
        require(client.localIdentity != null) { "parsec client must have private key set" }
        registerSuperloginExceptions(client.localInterface)
        jobs += globalLaunch {
            client.connectedStateFlow.collect { on ->
                if (on) tryRestoreLogin()
                else {
                    adapterReady.value = false
                }
            }
        }
        saveState?.let { save ->
            jobs += globalLaunch {
                _state.collect {
                    @Suppress("UNCHECKED_CAST")
                    when (it) {
                        is LoginState.LoggedIn<*> -> {
                            lastKiloginData = it.clientData as ClientSecretData<D>
                            save(
                                biEncode(
                                    loginDataSerializer,
                                    it.clientData
                                )
                            )
                        }

                        LoginState.LoggedOut -> {
                            lastKiloginData = null
                            save(null)
                        }
                    }
                }
            }
        }
        client.localInterface.apply {
            onConnected(true) {
                session.client = this@KiloginClient
            }
        }
    }

    @Suppress("unused")
    fun close() {
        client.close()
    }

    /**
     * Force dropping and re-establish underlying parsec3 connection and restore
     * login state to the current.
     */
//    fun reconnect() {
//        adapterReady.value = false
//        client.reconnect()
//    }

//    private var registration: Registration? = null

    /**
     * Whether the client is supposed to be logged in. Note that it is also true when
     * there is no ready connection (means also offline), if there is information about staved
     * loged in state. It can change at aby time as server may drop login state too. Use
     * [state] flow to track the state changes and [adapterReady] flow to track connection state
     * that are in fact independent to some degree.
     */
    val isLoggedIn get() = state.value.isLoggedIn

    /**
     * The data storage key, a random key created when registering. It is retrieved automatically
     * on login/registration. TODO: check!
     */
    val dataKey: SymmetricKey? get() = (_state.value as? LoginState.LoggedIn<*>)?.clientData?.dataKey

    /**
     * Check that some login name is valid and not yet taken at the moment of calling.
     * It does not guarantee that it will be available later, e.g., when [register] will be called,
     * so its return value should also be processed.
     *
     * This function is intended to validate login name field in UI
     */
    suspend fun isLoginNameAvailable(name: String): Boolean = call(slIsLoginNameAvailable, deriveLoginNameKey(name))

    /**
     * Perform registration and login attempt and return the result. It automatically caches and reuses intermediate
     * long-calculated keys so it runs much faster when called again with the same password.
     *
     *  _Client should be in logged-out state!_ Use [logout] as needed
     *
     * If result is [Registration.Result.Success], user state is _logged in_.
     *
     * @throws IllegalStateException if logged in
     */
    suspend fun registerWithPackedData(
        loginName: String, password: String,
        packedRegistrationData: UByteArray? = null,
        passwordComplexity: KDF.Complexity = KDF.Complexity.Moderate,
        secretComplexity: KDF.Complexity = KDF.Complexity.Sensitive,
        packedSecretData: UByteArray? = null
    ): Registration.Result {
        mustBeLoggedOut()
        val registration = Registration(client, passwordComplexity, secretComplexity, { deriveLoginNameKey(it) })
        val result = registration.registerWithData(loginName, password, packedRegistrationData, packedSecretData)
        // if successful, state is changed to logged in
        if (result is Registration.Result.Success) {
            _state.value = LoginState.LoggedIn(
                ClientSecretData(
                    loginName,
                    localIdSecretKey,
                    result.dataKey,
                    result.encodedData?.let { biDecode(dataSerializer, it) },
                    packedSecretData
                )
            )
        }
        return result
    }

    /**
     * Register a new account using login name, password, arbitrary registration data that will be passed
     * to the server registration interface via [RegistrationArgs.packedApplicationData]. See [KiloginServerSession].
     */
    suspend inline fun <reified R> register(
        loginName: String, password: String,
        registrationData: R?,
        passwordComplexity: KDF.Complexity = KDF.Complexity.Moderate,
        secretComplexity: KDF.Complexity = KDF.Complexity.Sensitive,
    ): Registration.Result {
        return registerWithPackedData(
            loginName, password,
            registrationData?.let { biEncode(it) },
            passwordComplexity, secretComplexity,
            null
        )
    }

    suspend inline fun <reified R, reified SecretData> register(
        loginName: String, password: String,
        registrationData: R?,
        passwordComplexity: KDF.Complexity = KDF.Complexity.Moderate,
        secretComplexity: KDF.Complexity = KDF.Complexity.Sensitive,
        secretData: SecretData
    ): Registration.Result {
        return registerWithPackedData(
            loginName, password,
            registrationData?.let { biEncode(it) },
            passwordComplexity, secretComplexity,
            biEncode(secretData)
        )
    }

    private fun extractData(rr: UByteArray?): D? = rr?.let { biDecode(dataSerializer, it) }

    private fun mustBeLoggedOut() {
        if (isLoggedIn)
            throw IllegalStateException("please log out first")
    }

    private fun mustBeLoggedIn() {
        if (!isLoggedIn)
            throw IllegalStateException("please log in first")
    }

    @Suppress("UNCHECKED_CAST")
    fun loginDataOrThrow(): ClientSecretData<D> =
        (_state.value as? LoginState.LoggedIn<D>)?.clientData ?: throw IllegalStateException("not logged in")

    suspend fun logout() {
        if (isLoggedIn) {
            call(slLogout)
            _state.value = LoginState.LoggedOut
        }
    }

    /**
     * Try to log in by login name and password.
     *
     * @return updated login data (and new token value) or null if login is not possible
     *         (wrong password, login name, or no connection)
     */
    suspend fun loginByPassword(loginName: String, password: String): ClientSecretData<D>? {
        mustBeLoggedOut()
        // Request derivation params
        val params = call(slRequestDerivationParams, deriveLoginNameKey(loginName))
        val keys = DerivedKeys.derive(password, params)
        // Request login data by derived it
        return call(
            slRequestACOByLoginName,
            RequestACOByLoginNameArgs(deriveLoginNameKey(loginName), keys.loginId)
        ).let { loginRequest ->
            try {
                AccessControlObject.unpackWithKey<RestoreAccessPayload>(
                    loginRequest.packedACO,
                    keys.loginAccessKey
                )?.let { aco ->
                    val result = call(
                        slLoginByKey,
                        SealedBox.encode(
                            LoginByPasswordPayload(deriveLoginNameKey(loginName), client.token()),
                            aco.payload.loginPrivateKey,
                        )
                    )
                    if (result is AuthenticationResult.Success) {
                        val kld = ClientSecretData(
                            loginName,
                            localIdSecretKey,
                            // this is data key, it is stored in ACO!
                            aco.payload.dataStorageKey,
                            biDecode(dataSerializer, result.extraData),
                            aco.payload.packedSecretData
                        )
                        _state.value = LoginState.LoggedIn(kld)
                        kld
                    } else null
                }
            } catch (t: Throwable) {
                debug { "error while signign in: $t" }
                null
            }
        }
    }

    /**
     * Resets password and log in using a `secret` string (one that wwas reported on registration. __Never store
     * secrt string in your app__. Always ask user to enter it just before the operation and wipe it out
     * immediately after. It is a time-consuming procedure. Note that on success the client state changes
     * to [LoginState.LoggedIn].
     *
     * @param secret the secret string as was reported when registering
     * @param newPassword new password (apply strength checks, it is not checked here)
     * @param complexity complexity of the password derivation; it is possible to change the strength here
     * @return login data instance on success or null
     */
    suspend fun resetPasswordAndSignIn(
        secret: String, newPassword: String,
        complexity: KDF.Complexity = KDF.Complexity.Moderate,
    ): ClientSecretData<D>? {
        mustBeLoggedOut()
        return try {
            val (id, key) = RestoreKey.parse(secret, complexity)
            val packedACO = call(slRequestACOBySecretId, id)
            AccessControlObject.unpackWithKey<RestoreAccessPayload>(packedACO, key)?.let {
                if (changePasswordWithACO(it, newPassword, complexity))
                    @Suppress("UNCHECKED_CAST")
                    (_state.value as LoginState.LoggedIn<D>)?.clientData
                else null
            }
        } catch (_: RestoreKey.InvalidSecretException) {
            null
        } catch (x: Exception) {
            x.printStackTrace()
            null
        }
    }

    private var lastLoginName: String? = null

    /**
     * Changes the password (which includes generating new login key). Does not require any particular
     * [state]. This is a long operation. On success, it changes (updates) [state] to [LoginState.LoggedIn]
     * with new data whatever it was before. Be aware of it.
     */
    protected suspend fun changePasswordWithACO(
        aco: AccessControlObject<RestoreAccessPayload>,
        newPassword: String,
        complexity: KDF.Complexity = KDF.Complexity.Sensitive,
    ): Boolean {
        return coroutineScope {
            // Get current nonce in parallel
            val nonce = client.token()

            // get login key in parallel
            val newLoginKey = SigningSecretKey.new()
            // derive keys in main scope
            val keys = DerivedKeys.derive(newPassword, complexity)

            // new ACO payload: new login key, old data storage key and login
            val newSlp = RestoreAccessPayload(
                aco.payload.login,
                newLoginKey,
                aco.payload.dataStorageKey,
                aco.payload.packedSecretData
            )
            // new ACO with a new password key and payload (but the same secret!)
            val newAco = aco.updatePasswordKey(keys.loginAccessKey).updatePayload(newSlp)
            // trying to update
            val result = call(
                slChangePasswordAndLogin,
                ChangePasswordArgs(
                    deriveLoginNameKey(aco.payload.login),
                    SealedBox.encode(
                        ChangePasswordPayload(
                            newAco.packed, keys.pbkdParams, newLoginKey.verifyingKey, keys.loginId, nonce
                        ),
                        aco.payload.loginPrivateKey,
                    )
                )
            )
            when (result) {
                is AuthenticationResult.Success -> {
                    _state.value = LoginState.LoggedIn(
                        ClientSecretData(
                            aco.payload.login,
                            localIdSecretKey,
                            aco.payload.dataStorageKey,
                            biDecode(dataSerializer, result.extraData),
                        )
                    )
                    true
                }

                else -> {
                    warning { "Change password result: $result" }
                    false
                }
            }
        }
    }

    /**
     * Change password for a logged-in user using its known password. It is a long operation
     * @param oldPassword existing password (re-request it from a user!)
     * @param newPassword new password. we do not chek it but it should be strong - check it on your end
     *                    for example with [net.sergeych.unikrypto.Passwords] tools
     * @return true if the password has been successfully changed
     */
    suspend fun changePassword(
        oldPassword: String, newPassword: String,
        complexity: KDF.Complexity = KDF.Complexity.Moderate,
    ): Boolean {
        val kld = loginDataOrThrow()
        val loginNameKey = deriveLoginNameKey(kld.loginName)
        val dp = call(slRequestDerivationParams, loginNameKey)
        val keys = DerivedKeys.derive(oldPassword, dp)
        val data = call(slRequestACOByLoginName, RequestACOByLoginNameArgs(loginNameKey, keys.loginId))
        return AccessControlObject.unpackWithKey<RestoreAccessPayload>(data.packedACO, keys.loginAccessKey)
            ?.let {
                changePasswordWithACO(it, newPassword, complexity)
            } ?: false
    }

    private val derivedLoginNameCache = mutableMapOf<String, ByteChunk>()

    /**
     * Login derivation KDF must be fixed and does non depend on crypto upgrades,
     * as we can't associate its parameters with a login name - it s not disclosed!
     */
    private val loginNameKDF: KDF.Argon = KDF.Argon.create(
        KDF.Complexity.Interactive,
        KDF.Argon.deriveSaltFromString(loginNameSalt),
        32
    )

    /**
     * Convert login name to login key used in the system caching it for future calls
     */
    fun deriveLoginNameKey(loginName: String): ByteChunk = derivedLoginNameCache.getOrPut(loginName) {
        loginNameKDF.deriveKey(loginName).asChunk()
    }

    companion object {
        /**
         * Create new Kilogin clint using a builder.
         * @param savedState saved state if kept from previous login, see [KiloginClientBuilder.saveState]
         */
        inline fun <reified D, S : KiloginClientSession> create(
            savedState: UByteArray? = null,
            f: KiloginClientBuilder<S>.() -> Unit
        ): KiloginClient<D, S> {
            return KiloginClientBuilder<S>().apply(f).build(serializer<D>(), savedState)
        }
    }

}